import React from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from '../../lib/utils';
import MembershipCard from './MembershipCard';
import { useCenterFeatureFlags } from '../../hooks/useCenterFeatureFlags';
import {
    selectSharedParticipantEntitlements,
    StaffMembershipEntitlement,
    useMemberSharedEntitlements,
} from '../../features/sharedMembership';

type Props = {
    memberId: string;
    centerId: string;
    /** 담당 강사 이름 표시용. 프로필이 이미 들고 있는 배정 강사 목록을 그대로 넘긴다. */
    coachNameById?: Record<string, string>;
    /** 읽기 전용 회원이면 수업 등록 진입점을 막는다. */
    isReadOnly?: boolean;
};

/**
 * 공유받아 쓰는 수강권을 소유 수강권과 같은 카드로 보여준다.
 *
 * 참여자에게는 Membership 문서가 없어 본인 소유 목록(fetchAllMembershipForMember)에는
 * 나오지 않는다. 사용 권리 API로 따로 읽어 같은 카드 모양으로 그리고, 구분은 헤더의
 * "공유 · ○○님" 배지로만 준다 — 알아야 할 건 "누구와 공유 중인가" 하나다.
 */
const SharedMembershipReceivedSection = ({
    memberId,
    centerId,
    coachNameById,
    isReadOnly = false,
}: Props) => {
    const navigate = useNavigate();
    const { isSharedMembershipReadEnabled } = useCenterFeatureFlags();
    const entitlementsQuery = useMemberSharedEntitlements({
        memberId,
        centerId,
        enabled: isSharedMembershipReadEnabled,
    });

    if (!isSharedMembershipReadEnabled) return null;

    const sharedEntitlements = selectSharedParticipantEntitlements(entitlementsQuery.data);
    // 공유받은 수강권이 없으면 영역 자체를 만들지 않는다. 대부분의 회원이 여기 해당한다.
    if (sharedEntitlements.length === 0) return null;

    /** 카드가 읽는 필드로 맞춘다. remainSession 은 소유자와 함께 쓰는 예약 가능 회차다. */
    const toCardMembership = (entitlement: StaffMembershipEntitlement) => ({
        _id: entitlement.membership.id,
        name: entitlement.membership.name,
        // 카드의 '시간' 칸이 읽는 필드. 빠뜨리면 'undefined분'으로 그려진다.
        period: entitlement.membership.period,
        totalSession: entitlement.membership.totalSession,
        remainSession: entitlement.usage.bookableRemaining,
        currentSession: entitlement.usage.usedCount,
        activationDate: entitlement.membership.activationDate,
        expirationDate: entitlement.membership.expirationDate,
        coachId: entitlement.membership.coachId,
        centerId: entitlement.membership.centerId,
        status: 'active',
        // 정지 배지용(이용권 요약). 빠뜨리면 공유받은 카드에 정지가 안 보인다.
        pause: entitlement.membership.pause,
    });

    return (
        <Col style={{ marginBottom: 32 }}>
            <Row className="items-center gap-[8px]" style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>공유받은 수강권</span>
                <span
                    style={{
                        padding: '2px 8px',
                        borderRadius: 999,
                        backgroundColor: '#E8F8F5',
                        color: '#008977',
                        fontSize: 12,
                        fontWeight: 700,
                    }}
                >
                    {sharedEntitlements.length}건
                </span>
            </Row>
            {/*
              위 목록(본인 소유)과 규칙이 다른 카드다. 왜 여기만 따로 있고 무엇이 다른지를
              한 줄로 밝혀 두지 않으면 "내 수강권인데 왜 못 고치지"에서 막힌다.
            */}
            <span style={{ fontSize: 12, lineHeight: '18px', color: '#6B7280', marginBottom: 12 }}>
                다른 회원의 수강권을 함께 쓰는 중입니다. 회차는 소유자와 나눠 쓰고, 수정·환불은 소유자
                프로필에서만 할 수 있습니다.
            </span>
            <Row className="flex flex-row justify-start items-start overflow-x-auto">
                {sharedEntitlements.map((entitlement, index) => (
                    <MembershipCard
                        key={entitlement.membership.id}
                        index={index}
                        membership={toCardMembership(entitlement) as any}
                        sharedOwnerName={entitlement.sharedOwner?.name}
                        // 공유 회차는 소유자와 나눠 쓰므로 '잔여'가 아니라 지금 예약할 수 있는 수다.
                        remainLabel="예약 가능"
                        // PT 공유는 담당 강사가 있어야 참여할 수 있다. 위 목록이 강사별로 묶여 있어
                        // 여기에 강사가 없으면 "배정 안 된 수강권"처럼 읽힌다.
                        coachName={coachNameById?.[String(entitlement.membership.coachId)]}
                        // 공유받은 수강권으로 이 회원의 수업을 바로 잡는다.
                        // 소유자 상세를 거쳐 회원을 다시 고르게 하면 동선이 끊긴다.
                        onClickRegisterSchedule={
                            isReadOnly
                                ? undefined
                                : () =>
                                      navigate('/schedule/create', {
                                          state: {
                                              type: 'oneSchedule',
                                              selectedSchedule: {
                                                  memberId,
                                                  coachId: entitlement.membership.coachId,
                                                  coachName:
                                                      coachNameById?.[
                                                          String(entitlement.membership.coachId)
                                                      ] || '',
                                                  membershipId: entitlement.membership.id,
                                                  scheduleStartTime: dayjs().format('YYYY-MM-DD HH:mm'),
                                              },
                                              calendarType: 'daily',
                                              calendarSelectedDay: dayjs().format('YYYY-MM-DD'),
                                              calendarScrollY: 0,
                                              returnPath: `/member/${memberId}`,
                                              returnState: { memberId },
                                          },
                                      })
                        }
                        // 원본은 소유자의 수강권이다. 상세도 소유자 화면으로 보낸다.
                        // coachName까지 넘겨야 한다 — 상세에서 '수업 등록 바로가기'로 넘어갈 때
                        // 이 값이 그대로 담당 강사 라벨이 되고, 없으면 강사 칸이 빈 채로 열린다.
                        onClickDetailPage={() =>
                            navigate('/member/detail/membership', {
                                state: {
                                    membershipId: entitlement.membership.id,
                                    memberId: entitlement.sharedOwner?.memberId,
                                    memberName: entitlement.sharedOwner?.name,
                                    coachId: entitlement.membership.coachId,
                                    coachName:
                                        coachNameById?.[String(entitlement.membership.coachId)] || '',
                                },
                            })
                        }
                    />
                ))}
            </Row>
        </Col>
    );
};

export default SharedMembershipReceivedSection;
